import type {
  APPROVAL_STATUS,
  DASHBOARD_STATUS,
  FORM_YES_NO,
  SHIPMENT_STATUS,
  SIMPLE_STATUS,
  LOGISTICS_TYPE,
} from './constants/project';
import { BYOL_LOGISTICS_TYPES, PROJECT_STATUS } from './constants/project';

export type ObjectValues<T> = T[keyof T] | (string & {});

export type ProjectStatus = ObjectValues<typeof PROJECT_STATUS>;
export type FormYesNo = ObjectValues<typeof FORM_YES_NO>;
export type SimpleStatus = ObjectValues<typeof SIMPLE_STATUS>;
export type ApprovalStatus = ObjectValues<typeof APPROVAL_STATUS>;
export type ShipmentStatus = ObjectValues<typeof SHIPMENT_STATUS>;
export type DashboardStatus = ObjectValues<typeof DASHBOARD_STATUS>;

export interface FileUploadFormValues {
  files: File[];
  instructions: string;
  name: string;
}

export interface ApprovalFlowFormValues extends FileUploadFormValues {
  notes: string;
  linkUrl?: string;
  mockLinkUrl?: string;
  status?: ApprovalStatus;
  uploadLink?: string;
}

export function isProjectStatus(status: unknown): status is ProjectStatus {
  return (Object.values(PROJECT_STATUS) as string[]).includes(String(status));
}

export type LogisticsType = (typeof LOGISTICS_TYPE)[keyof typeof LOGISTICS_TYPE];

export const isByolLogisticsType = (logisticsType?: string[] | readonly string[] | null): boolean =>
  Array.isArray(logisticsType) &&
  logisticsType.some((value) =>
    BYOL_LOGISTICS_TYPES.includes(value as (typeof BYOL_LOGISTICS_TYPES)[number]),
  );

// Canonical logistics array source of truth.
export const getProjectLogisticsTypes = (
  logisticsType?: string[] | readonly string[] | null,
): string[] => {
  if (Array.isArray(logisticsType) && logisticsType.length > 0) {
    return [...logisticsType];
  }
  return [];
};

export type {
  Admin,
  AdminProject,
  Customer,
  Organization,
  AggregationType,
  AssetWithUrls,
  AssignConfigBlockRequest,
  AssignSolutionSurveyDesign,
  AudienceBuilderAnswer,
  AudienceBuilderAudienceBuilderAudience,
  AudienceBuilderLegacyAudience,
  AudienceBuilderQuota,
  AudienceBuilderQuotaPartition,
  AudienceBuilderRecruitStatus as RecruitStatus,
  AudienceBuilderScreenerQuestion,
  AudienceBuilderScreenerQuestionGroup,
  AudienceBuilderScreenerResponse,
  AudienceBuilderTestGroup,
  AudienceBuilderTestGroupProduct,
  AudienceBuilderTestGroupTask,
  BannerPoint,
  FeasibilityTestGroup,
  FeasibilityQuota,
  FeasibilityQuotaPartition,
  FeasibilityQuotaAnswers,
  FeasibilityRecruitCountRequest,
  FeasibilityRecruitingFilter,
  FeasibilityInternalRecruitingOptions,
  Block,
  BlockBehaviorValue,
  BlockGroupBehaviorValue,
  BlockSlotsDto,
  BlueprintCatalog,
  BlueprintCatalogApplyJob,
  BlueprintKey,
  CatalogBlock,
  CatalogBlueprintKey,
  CatalogModule,
  CatalogModuleConfiguration,
  CatalogQuestionDesign,
  CatalogSolution,
  CatalogSurveyRole,
  ChatConversationResponse,
  ChatConversationSummary,
  ChatMessageResponse,
  Community,
  CompositeSlot,
  ConceptTestAnswers,
  ConfigureSlotValueRequest,
  CrossTab,
  CrossTabAggregation,
  CrossTabEntry,
  CrossTabMetadata,
  CrossTabParams,
  CrossTabsDto,
  DisqualificationLogicAllowedOpsResponse,
  DisqualificationLogic,
  EntryDataValue,
  Experience,
  ExperienceType,
  ExploreBannerRead,
  ExploreItemResult,
  ExploreRankResult,
  ExploreTextAnswerResult,
  FieldingGroup,
  GetSummaryResponse,
  Insert,
  KeyValuePair602,
  Kit,
  Module,
  ModuleConfiguration,
  ObjectiveDto,
  PhysicalProductTestAnswers,
  PostProjectsByProjectIdAttachmentsApiArg as UploadProjectAttachmentRequest,
  PostProjectsByProjectIdAudienceAttachmentsApiArg,
  PostProjectsByProjectIdSurveyConfigBlocksAndSurveyConfigBlockIdSlotValuesV2ApiArg as AssignSlotValueRequest,
  PostProjectsByProjectIdSurveyConfigsAndSurveyConfigIdQuestionsSurveyConfigQuestionIdChoicesSurveyConfigQuestionChoiceIdApiArg as UpdateSurveyConfigChoiceRequest,
  Product,
  ProductGroupDto,
  ProductHandlingPreference,
  ProductList,
  ProductListEntry,
  ProductProjectAsset,
  Project,
  ProjectAttachment,
  ProjectConfigurationDto,
  ProjectHackyConfigurationDto,
  ProjectIntakeAnswers,
  ProjectObjectiveResponse,
  ProjectRecruitSource,
  ProjectReviewResult,
  ProjectSummaryResponse,
  ProjectTypeOption,
  Question,
  QuestionChoice,
  QuestionDesign,
  QuestionDesignChoice,
  QuestionValidation,
  RecruitingData,
  ResultsMeta,
  ResultContextRead,
  ReviewIssue,
  ReviewRecommendation,
  ScorecardAggregationOverride,
  PenaltyAnalysisDto,
  AlienationAnalysisResponse,
  ScorecardDataDto,
  ScorecardEntryData,
  ScorecardEntryMetadata,
  Segment,
  SegmentCount,
  SegmentRule,
  SegmentRuleAllowedOps,
  SegmentRuleGroup,
  ShareLink,
  ShareLinkDefn,
  Shipment,
  ShowLogic,
  ShowLogicAllowedOpsResponse,
  Slot,
  SlotValue,
  Solution,
  SolutionSurveyDesign,
  SurveyAnswers,
  SurveyBundle,
  SurveyConfig,
  SurveyConfigBlock,
  SurveyConfigBlockGroup,
  SurveyConfigBlockSlotValues,
  SurveyConfigDto,
  SurveyConfigDtoRead,
  SurveyConfigQuestion,
  SurveyConfigQuestionChoice,
  SurveyConfigQuestionRouting,
  SurveyExpression,
  SurveyGroup,
  SurveyGroupDto,
  Team,
  TestCategory,
  SnapshotInputs,
  SnapshotLineItem,
  SubmittedCostSnapshot,
  UpdateProjectRequest,
  UpdateTimelineRequest,
} from './rtk/rtk.generated';

import type { components } from './fetch/fetch.generated';

type FetchSchemas = components['schemas'];

export type ConfigureBlockRequest = FetchSchemas['ConfigureBlockRequest'];
export type ConfigureProjectV2Request = FetchSchemas['ConfigureProjectV2Request'];
export type ConfigureSurveyByTypeRequest = FetchSchemas['ConfigureSurveyByTypeRequest'];
export type ScorecardParams = FetchSchemas['ScorecardParams'];
export type TestClassification = FetchSchemas['TestClassification'];
export type PresentationRuleDto = FetchSchemas['PresentationRuleDto'];
export type ProjectDescriptionProduct = FetchSchemas['ProjectDescriptionProduct'];
export type ProjectDescriptionQuestion = FetchSchemas['ProjectDescriptionQuestion'];
export type ProjectDescriptionResponse = FetchSchemas['ProjectDescriptionResponse'];
